/**
 * The instant an appointment happens, and how to spell it.
 *
 * Twin of `blhClinicApp/src/services/schedule/appointmentClock.ts`, written
 * against `Intl` because this app is on date-fns 2 and carries no `@date-fns/tz`.
 * Keep the two in step: same names, same contracts.
 *
 * Why it exists here: every date this app showed or exported was rebuilt by
 * parsing the legacy `date` / `time` strings — trying "MM/DD/YYYY" first (a
 * format we do not store), then `new Date("Monday, September 14, 2026")`, which
 * yields midnight in the viewer's zone and throws the appointment's real hour
 * away. `startAt` was never consulted, even though it is the exact instant and
 * always present: across all twelve tenants, 0 of 21,397 appointments lack it.
 *
 * `date` is a legacy mirror on its way out. Some rows carry an epoch in it, so
 * anything that reads it renders "1777498200000". Read `startAt`.
 */

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * The appointment's instant, in epoch milliseconds.
 *
 * `startAt` is canonical; `ISOdate` is its older mirror (also numeric). The
 * legacy `date` string is deliberately NOT consulted — it cannot express a time
 * of day reliably, and that is the whole reason this helper exists.
 *
 * @returns {number|null} epoch ms, or null when the row carries no timestamp
 */
export function appointmentInstant(appointment) {
  for (const candidate of [appointment?.startAt, appointment?.ISOdate]) {
    const at = Number(candidate);
    if (Number.isFinite(at) && at > 0) return at;
  }
  return null;
}

/**
 * Fixed-offset abbreviations found on real location records, read as the zone
 * they were meant to name: "MST" does not follow daylight saving. The same
 * table as blhWebServer/src/domain/time/timeZone.js.
 */
const ABBREVIATION_TO_IANA = Object.freeze({
  MST: "America/Edmonton",
  MDT: "America/Edmonton",
  PST: "America/Vancouver",
  PDT: "America/Vancouver",
  EST: "America/Toronto",
  EDT: "America/Toronto",
  AST: "America/Halifax",
  ADT: "America/Halifax",
  CST: "America/Winnipeg",
  CDT: "America/Winnipeg",
  NST: "America/St_Johns",
  NDT: "America/St_Johns",
});

/** Whatever is stored, as a usable IANA zone, or null. */
export function normalizeTimeZone(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed === "UTC") return "UTC";
  if (trimmed.includes("/")) {
    try {
      new Intl.DateTimeFormat("en-US", {timeZone: trimmed});
      return trimmed;
    } catch {
      return null;
    }
  }
  return ABBREVIATION_TO_IANA[trimmed.toUpperCase()] || null;
}

/**
 * The zone an appointment's wall time is expressed in: its location's.
 *
 * The session bootstrap sends `locations` with an exclusion projection, so
 * `timeZone` is already on every location this app holds.
 *
 * @returns {string|null} an IANA zone name, or null for "use the viewer's"
 */
export function resolveAppointmentTimeZone({
  appointment = null,
  locations = null,
  location = null,
  organization = null,
} = {}) {
  const zoneOf = (holder) =>
    normalizeTimeZone(holder?.timeZone) || normalizeTimeZone(holder?.timezone);

  const fromLocation = zoneOf(location);
  if (fromLocation) return fromLocation;

  const lid = appointment?.lid || null;
  if (lid && Array.isArray(locations)) {
    const match = locations.find((l) => String(l?.lid) === String(lid));
    const zone = zoneOf(match);
    if (zone) return zone;
  }

  return zoneOf(organization);
}

// The calendar fields of an instant, read on a given clock.
function partsOn(timestamp, timeZone) {
  const at = Number(timestamp);
  if (!Number.isFinite(at) || at <= 0) return null;

  const formatter = new Intl.DateTimeFormat("en-US", {
    ...(timeZone ? {timeZone} : {}),
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
  });

  const parts = {};
  for (const part of formatter.formatToParts(new Date(at))) {
    parts[part.type] = part.value;
  }
  if (!WEEKDAYS.includes(parts.weekday)) return null;
  // `hour12: false` renders midnight as "24" in some ICU versions.
  const hour = Number(parts.hour) % 24;
  return {
    weekday: parts.weekday,
    month: parts.month,
    day: Number(parts.day),
    year: Number(parts.year),
    hour,
    minute: Number(parts.minute),
  };
}

/** "Monday, September 14, 2026" on the clinic's clock. */
export function legacyDateString(timestamp, timeZone = null) {
  const p = partsOn(timestamp, timeZone);
  if (!p) return "";
  return `${p.weekday}, ${p.month} ${p.day}, ${p.year}`;
}

/** "7:00 PM" on the clinic's clock. */
export function legacyTimeString(timestamp, timeZone = null) {
  const p = partsOn(timestamp, timeZone);
  if (!p) return "";
  const suffix = p.hour >= 12 ? "PM" : "AM";
  const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${hour12}:${String(p.minute).padStart(2, "0")} ${suffix}`;
}
